/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useMemo } from 'react';

import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { MenuGroup } from '@atlaskit/menu';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useUCMenuItems } from '@atlassian/navigation-bff-data-provider/universal-create';

import { MAX_ALLOWED_SECTIONS } from '../../../constants';
import { useMenuControl } from '../../../context';
import { withIntlProvider } from '../../../services';
import ItemWrapper from '../MenuItem/item-wrapper';
import { RenderableMenuSection } from '../RenderableMenuSection';
import { SkeletonItem } from '../SkeletonItem';

import { sectionsToAppMenuConfig } from './utils/configConverters';
import { computeRenderableSections, computeSectionMetadata } from './utils/sectionComputations';

const styles = cssMap({
	menuWrap: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

function MenuContentV2Inner() {
	const { locale } = useIntl();
	const { sections, crossAppCreationConfig, product, subProduct } = useMenuControl();
	const {
		cloudId = '',
		orgId = '',
		accountId = '',
	} = crossAppCreationConfig?.enabled ? crossAppCreationConfig : {};

	const appMenuConfig = useMemo(() => sectionsToAppMenuConfig(sections), [sections]);

	const { data, isLoading, hasError, fetchUCMenuItems } = useUCMenuItems({
		cloudId,
		orgId,
		product,
		subProduct,
		userId: accountId,
		locale,
		appMenuConfig,
		skipInitialization: !crossAppCreationConfig?.enabled,
	});

	// Fetch menu items when cross-app creation is enabled
	useEffect(() => {
		if (crossAppCreationConfig?.enabled) {
			fetchUCMenuItems();
		}
	}, [crossAppCreationConfig?.enabled, fetchUCMenuItems]);

	const renderableSections = useMemo(
		() => computeRenderableSections(sections, data, hasError),
		[sections, data, hasError],
	);

	const sectionsToAnalyze = useMemo(
		() => renderableSections.slice(0, MAX_ALLOWED_SECTIONS),
		[renderableSections],
	);

	// Pre-compute section metadata (start indices and total count) to avoid O(n²) complexity
	const sectionMetadata = useMemo(
		() => computeSectionMetadata(sectionsToAnalyze),
		[sectionsToAnalyze],
	);

	// Only show loading state when cross-app creation is enabled
	// This ensures native-only items render immediately when cross-app is disabled
	if (isLoading && crossAppCreationConfig?.enabled) {
		return (
			<MenuGroup spacing="compact" testId="global-create-content-popover-menu-v2-loading">
				<Box xcss={cx(styles.menuWrap)}>
					<ItemWrapper>
						<SkeletonItem testId="loading-menu-content-v2" />
					</ItemWrapper>
				</Box>
			</MenuGroup>
		);
	}

	if (sectionsToAnalyze.length === 0) {
		return null;
	}

	return (
		<MenuGroup spacing="compact" testId="global-create-content-popover-menu-v2">
			<Box xcss={cx(styles.menuWrap)}>
				{sectionsToAnalyze.map((section, sectionIndex) => (
					<RenderableMenuSection
						section={section}
						sectionIndex={sectionIndex}
						totalMenuItemsCount={sectionMetadata.totalCount}
						sectionStartIndex={sectionMetadata.startIndices[sectionIndex]}
						key={`menu-section-${section.type}-${sectionIndex}`}
					/>
				))}
			</Box>
		</MenuGroup>
	);
}

const MenuContentV2WithIntl = withIntlProvider(MenuContentV2Inner);

export const MenuContentV2 = () => (
	<ErrorBoundary fallback={null}>
		<MenuContentV2WithIntl />
	</ErrorBoundary>
);
